import { safeLocalStorage } from '../utils/safeLocalStorage';

const TEAM_INVITE_REDIRECT_STORAGE_KEY = 'pending_team_invite_redirect';
const TEAM_INVITE_REDIRECT_TTL_MS = 24 * 60 * 60 * 1000;
const TEAM_INVITE_QUERY_KEYS = ['inviteCode', 'invite', 'code'];
const TEAM_INVITE_WITH_CODE_PATH_REGEX = /^\/home\/teams\/invite\/[^/?#]+\/?$/;
const TEAM_INVITE_BASE_PATH_REGEX = /^\/home\/teams\/invite\/?$/;

type PendingTeamInviteRedirect = {
  path: string;
  createdAt: number;
};

const hasInviteCodeInSearch = (search: string) => {
  const normalizedSearch = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(normalizedSearch);

  return TEAM_INVITE_QUERY_KEYS.some((key) => {
    return Boolean(searchParams.get(key)?.trim());
  });
};

const splitPath = (path: string) => {
  const [pathWithoutHash] = path.split('#');
  const queryStartIndex = pathWithoutHash.indexOf('?');

  if (queryStartIndex < 0) {
    return {
      pathname: pathWithoutHash,
      search: '',
    };
  }

  return {
    pathname: pathWithoutHash.slice(0, queryStartIndex),
    search: pathWithoutHash.slice(queryStartIndex),
  };
};

const parsePendingInviteRedirect = (): PendingTeamInviteRedirect | null => {
  const rawValue = safeLocalStorage.getItem(TEAM_INVITE_REDIRECT_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as PendingTeamInviteRedirect;
    if (!parsedValue.path || typeof parsedValue.createdAt !== 'number') {
      return null;
    }

    return parsedValue;
  } catch {
    return null;
  }
};

export const isTeamInvitePath = (pathname: string, search = '') => {
  if (TEAM_INVITE_WITH_CODE_PATH_REGEX.test(pathname)) {
    return true;
  }

  if (TEAM_INVITE_BASE_PATH_REGEX.test(pathname)) {
    return hasInviteCodeInSearch(search);
  }

  return false;
};

export const setPendingTeamInviteRedirect = (pathname: string, search = '') => {
  if (!isTeamInvitePath(pathname, search)) {
    return;
  }

  const pendingRedirect: PendingTeamInviteRedirect = {
    path: `${pathname}${search}`,
    createdAt: Date.now(),
  };

  safeLocalStorage.setItem(TEAM_INVITE_REDIRECT_STORAGE_KEY, JSON.stringify(pendingRedirect));
};

export const clearPendingTeamInviteRedirect = () => {
  safeLocalStorage.removeItem(TEAM_INVITE_REDIRECT_STORAGE_KEY);
};

export const getPendingTeamInviteRedirect = () => {
  const pendingRedirect = parsePendingInviteRedirect();
  if (!pendingRedirect) {
    clearPendingTeamInviteRedirect();
    return null;
  }

  const isExpired = Date.now() - pendingRedirect.createdAt > TEAM_INVITE_REDIRECT_TTL_MS;
  if (isExpired) {
    clearPendingTeamInviteRedirect();
    return null;
  }

  const { pathname, search } = splitPath(pendingRedirect.path);
  if (!isTeamInvitePath(pathname, search)) {
    clearPendingTeamInviteRedirect();
    return null;
  }

  return pendingRedirect.path;
};

export const consumePendingTeamInviteRedirect = () => {
  const pendingRedirectPath = getPendingTeamInviteRedirect();
  if (!pendingRedirectPath) {
    return null;
  }

  clearPendingTeamInviteRedirect();
  return pendingRedirectPath;
};
